import { setupWorker } from 'msw/browser';
import { http, HttpResponse, RequestHandler } from 'msw';
import {
  ApiContractor,
  ApiContractorRequest,
  ApiUserProfileRequest,
  ApiWorkOrderRequest,
} from '@allbin/mobilix-api-client';
import { DateTime } from 'luxon';
import { v4 } from 'uuid';

import { tdApiCheckInPlans } from '../testdata/checkins';
import { tdApiRecurringPlans } from '../testdata/recurring';
import { tdApiTemplates } from '../testdata/templates';
import { tdApiWorkorders, tdWorkorderTags } from '../testdata/workorders';
import { tdContractors } from '../testdata/contractors';
import { tdEntityEvents, tdGetApiWorkOrderEvents } from '../testdata/events';
import { tdEntityTypes, tdGetEntities } from '../testdata/entities';
import { tdErrorReports } from '../testdata/errorreports';
import { tdFilterSets } from '../testdata/filter';
import { tdGetEntityChangesets } from '../testdata/changesets';
import { tdSchema } from '../testdata/schema';
import {
  tdUserIdAdmin,
  tdUserIdContractorAdmin,
  tdUsers,
} from '../testdata/users';
import {
  TestUserWorkorderAdminPermissions,
  TestUserWorkorderContractorAdminPermissions,
} from '../testdata/permissions';
import { tdAllLicensesGroup } from '../testdata/licenses';
import { tdTenants } from '../testdata/tenants';
import { tdAllAuthProfiles } from '../testdata/profiles';
import { buildStubToken } from '../providers/StubAllAuthProvider';

import { scenarios } from './scenarios';

let profile = {};
// Mutable copy so create/update in the contractor dialog round-trip in mock
// mode; reset on reload like every other mocked collection.
let contractors: ApiContractor[] = [...tdContractors];

const gtfsBaseUrls = [
  'https://gtfs.vl.allbin.se',
  'https://gtfs-trafiklab-adapter.allbin.se/sormland',
  'https://gtfs-trafiklab-adapter.allbin.se/vasterbotten',
];

const gtfsTablePeriods = [
  {
    id: 1,
    name: 'MSW',
    provider: 'msw',
    date_start: '2020-01-01',
    date_end: null,
  },
];

const rests: RequestHandler[] = [
  ...gtfsBaseUrls.flatMap((baseUrl) => [
    http.get(`${baseUrl}/table_periods`, () =>
      HttpResponse.json(gtfsTablePeriods),
    ),
    http.get(`${baseUrl}/all_distinct_stops`, () => HttpResponse.json([])),
    http.get(`${baseUrl}/routes`, () => HttpResponse.json([])),
    http.get(`${baseUrl}/route_stops`, () => HttpResponse.json([])),
    http.get(`${baseUrl}/stop_link_shapes`, () => HttpResponse.json([])),
  ]),
  http.get('https://api.mobilix.dev.allbin.se/tags', () =>
    HttpResponse.json(tdWorkorderTags),
  ),
  http.get('https://api.mobilix.dev.allbin.se/workorders', () =>
    HttpResponse.json(tdApiWorkorders),
  ),
  http.post(
    'https://api.mobilix.dev.allbin.se/workorders',
    async ({ request }) => {
      const created = {
        ...((await request.json()) as ApiWorkOrderRequest),
        id: v4(),
        tenant_id: 'vl',
        meta: {
          created_at: DateTime.utc().toISO(),
          created_by: tdUserIdAdmin,
          updated_at: DateTime.utc().toISO(),
        },
      };
      // Kept so the list and the details page know the order after a reload.
      tdApiWorkorders.push(created);
      return HttpResponse.json([created]);
    },
  ),
  http.get<{ workOrderId: string }>(
    'https://api.mobilix.dev.allbin.se/workorders/:workOrderId',
    ({ params }) => {
      const wo = tdApiWorkorders.find((wo) => wo.id === params.workOrderId);
      if (!wo) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(wo);
    },
  ),
  http.get('https://api.mobilix.dev.allbin.se/entities', () =>
    HttpResponse.json(tdGetEntities()),
  ),
  http.post<{ entityId: string }>(
    'https://api.mobilix.dev.allbin.se/entities/:entityId/events',
    ({ params }) => {
      const events = tdEntityEvents.filter(
        (ev) => ev.entity_id === params.entityId,
      );
      if (events.length === 0) {
        return new HttpResponse(null, { status: 500 });
      }
      return HttpResponse.json(events[0]);
    },
  ),
  http.get('https://api.mobilix.dev.allbin.se/entity_types', () =>
    HttpResponse.json(tdEntityTypes),
  ),
  http.get('https://api.mobilix.dev.allbin.se/contractors', () =>
    HttpResponse.json(contractors),
  ),
  http.get<{ contractorId: string }>(
    'https://api.mobilix.dev.allbin.se/contractors/:contractorId',
    ({ params }) =>
      HttpResponse.json(contractors.find((c) => c.id === params.contractorId)),
  ),
  http.post<never, ApiContractorRequest>(
    'https://api.mobilix.dev.allbin.se/contractors',
    async ({ request }) => {
      const body = await request.json();
      const now = DateTime.now().toISO();
      const created: ApiContractor = {
        ...body,
        // Mirrors the API: `kind` defaults to `contractor` when omitted.
        kind: body.kind ?? 'contractor',
        id: crypto.randomUUID(),
        tenant_id: 'vl',
        meta: { created_at: now, created_by: tdUserIdAdmin, updated_at: now },
      };
      contractors = [...contractors, created];
      return HttpResponse.json(created, { status: 201 });
    },
  ),
  http.put<{ contractorId: string }, ApiContractorRequest>(
    'https://api.mobilix.dev.allbin.se/contractors/:contractorId',
    async ({ params, request }) => {
      const existing = contractors.find((c) => c.id === params.contractorId);
      if (!existing) {
        return new HttpResponse(null, { status: 404 });
      }
      const body = await request.json();
      // Mirrors the API: PUT replaces the record from the request, so an
      // optional field the form cleared (sent as undefined, dropped by JSON)
      // is cleared here too instead of keeping the stored value.
      const updated: ApiContractor = {
        ...body,
        // An omitted `kind` keeps the stored one.
        kind: body.kind ?? existing.kind,
        id: existing.id,
        tenant_id: existing.tenant_id,
        meta: { ...existing.meta, updated_at: DateTime.now().toISO() },
      };
      contractors = contractors.map((c) => (c.id === updated.id ? updated : c));
      return HttpResponse.json(updated);
    },
  ),
  http.get('https://api.mobilix.dev.allbin.se/tenants', () =>
    HttpResponse.json(tdTenants),
  ),
  // Mirrors the migrated API: `ids` is mandatory and unknown ids come back
  // as '<Deleted user>' placeholder rows, so lookups never 404.
  http.get('https://api.mobilix.dev.allbin.se/users', ({ request }) => {
    const idsParam = new URL(request.url).searchParams.get('ids');
    if (!idsParam) {
      return new HttpResponse(null, { status: 400 });
    }
    const users = idsParam.split(',').map(
      (id) =>
        tdUsers.find((u) => u.id === id) ?? {
          id,
          user_id: id,
          name: '<Deleted user>',
          email: '',
          meta: {
            created_at: DateTime.now().toISO(),
            created_by: id,
            updated_at: DateTime.now().toISO(),
          },
        },
    );
    return HttpResponse.json(users);
  }),
  http.get('https://api.mobilix.dev.allbin.se/entity_schemas', () =>
    HttpResponse.json([tdSchema]),
  ),
  http.get('https://api.mobilix.dev.allbin.se/feature_licenses', () =>
    HttpResponse.json([tdAllLicensesGroup]),
  ),
  http.get<{ entityId: string }>(
    'https://api.mobilix.dev.allbin.se/entities/:entityId/changesets',
    ({ params }) => {
      const entity = tdGetEntities().find((e) => e.id === params.entityId);
      if (!entity) {
        return new HttpResponse(null, { status: 404 });
      }
      const changesets = tdGetEntityChangesets(
        params.entityId,
        entity.properties,
      );
      if (!changesets) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(changesets);
    },
  ),
  http.get<{ entityId: string }>(
    'https://api.mobilix.dev.allbin.se/entities/:entityId/events',
    ({ params }) => {
      console.log('tdEntityEvents', tdEntityEvents);
      const events = tdEntityEvents.filter(
        (ev) => ev.entity_id === params.entityId,
      );
      return HttpResponse.json(events);
    },
  ),
  http.get<{ workOrderId: string }>(
    'https://api.mobilix.dev.allbin.se/workorders/:workOrderId/events',
    ({ params }) =>
      HttpResponse.json(tdGetApiWorkOrderEvents(params.workOrderId)),
  ),
  http.get('https://api.mobilix.dev.allbin.se/filtersets', () =>
    HttpResponse.json(tdFilterSets),
  ),
  http.get('https://api.mobilix.dev.allbin.se/user_profile', () =>
    HttpResponse.json({
      meta: {
        created_at: DateTime.now().toISO(),
        created_by: tdUserIdAdmin,
        updated_at: DateTime.now().toISO(),
      },
      profile,
    }),
  ),
  http.put<never, ApiUserProfileRequest>(
    'https://api.mobilix.dev.allbin.se/user_profile',
    async ({ request }) => {
      const body = await request.json();
      profile = body ? body.profile : profile;
      return HttpResponse.json({
        meta: {
          created_at: DateTime.now().toISO(),
          created_by: tdUserIdAdmin,
          updated_at: DateTime.now().toISO(),
        },
        profile,
      });
    },
  ),
  http.post(
    'https://api.mobilix.dev.allbin.se/error_reports',
    () => new HttpResponse(null, { status: 500 }),
  ),
  http.get('https://api.mobilix.dev.allbin.se/error_reports', () =>
    HttpResponse.json(
      tdErrorReports.filter((er) => er.meta.deleted_at === undefined),
    ),
  ),
  http.get<{ id: string }>(
    'https://api.mobilix.dev.allbin.se/error_reports/:id',
    ({ params }) => {
      const er = tdErrorReports.find((er) => er.id === params.id);
      if (!er) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(er);
    },
  ),
  http.get<{ id: string }>(
    'https://api.mobilix.dev.allbin.se/checkin_plans/:id',
    ({ params }) => {
      const plan = tdApiCheckInPlans.find((plan) => plan.id === params.id);
      if (!plan) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(plan);
    },
  ),
  http.post(
    'https://api.mobilix.dev.allbin.se/checkin_plans',
    () => new HttpResponse(null, { status: 500 }),
  ),
  http.put(
    'https://api.mobilix.dev.allbin.se/checkin_plans/:id',
    () => new HttpResponse(null, { status: 404 }),
  ),
  http.get('https://api.mobilix.dev.allbin.se/checkin_plans', () =>
    HttpResponse.json(tdApiCheckInPlans),
  ),
  http.get<{ id: string }>(
    'https://api.mobilix.dev.allbin.se/recurring_workorder_plans/:id',
    ({ params }) => {
      const plan = tdApiRecurringPlans.find((plan) => plan.id === params.id);
      if (!plan) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(plan);
    },
  ),
  http.get('https://api.mobilix.dev.allbin.se/recurring_workorder_plans', () =>
    HttpResponse.json(tdApiRecurringPlans),
  ),
  http.get('https://api.mobilix.dev.allbin.se/workorder_instructions', () =>
    HttpResponse.json(tdApiTemplates),
  ),
  // AllAuth endpoints — only exercised by the gallery's profile-selector
  // entry; app auth in msw/gallery modes is bypassed by StubAllAuthProvider.
  http.get('https://api.auth.dev.allbin.se/profiles', () =>
    HttpResponse.json(tdAllAuthProfiles),
  ),
  http.get('https://api.auth.dev.allbin.se/profiles/token', ({ request }) => {
    const profileId = new URL(request.url).searchParams.get('user_profile_id');
    const profile = tdAllAuthProfiles.find((p) => p.id === profileId);
    if (!profile) {
      return new HttpResponse(null, { status: 404 });
    }
    if (!profile.active) {
      return new HttpResponse(null, { status: 400 });
    }
    const isContractorProfile = profile.organization_id !== undefined;
    return HttpResponse.json({
      access_token: buildStubToken({
        sub: isContractorProfile ? tdUserIdContractorAdmin : tdUserIdAdmin,
        email: profile.email,
        tenant_id: profile.tenant_id,
        organization_id: profile.organization_id ?? null,
        permissions: isContractorProfile
          ? TestUserWorkorderContractorAdminPermissions
          : TestUserWorkorderAdminPermissions,
        user_profile_id: profile.id,
      }),
    });
  }),
];

function resolveScenarioOverrides(): RequestHandler[] {
  const name = new URLSearchParams(window.location.search).get('msw_scenario');
  if (!name) return [];
  const found = Object.hasOwn(scenarios, name) ? scenarios[name] : undefined;
  if (!found) {
    console.warn(
      `[msw] unknown scenario "${name}" — falling back to defaults (available: ${Object.keys(
        scenarios,
      ).join(', ')})`,
    );
    return [];
  }
  console.info(
    `[msw] scenario "${name}" active (${found.length} override handler${
      found.length === 1 ? '' : 's'
    })`,
  );
  return found;
}

export const mswWorker = setupWorker(...resolveScenarioOverrides(), ...rests);
